import {useCompanyTariffGuard} from '../../../../../../../modules/company-tariff';
import {closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors} from '@dnd-kit/core';
import {restrictToHorizontalAxis} from '@dnd-kit/modifiers';
import {SortableContext} from '@dnd-kit/sortable';
import AddIcon from '@mui/icons-material/Add';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import {IconButton, Stack} from '@mui/material';
import {findIndex} from 'lodash';
import {FC, useMemo, useState, useEffect} from 'react';
import {useFieldArray} from 'react-hook-form';
import {useStepsFormContext} from '../../contexts';
import {getNewFieldGroup} from '../../helpers';
import {useStepperStore} from '../../store/useStepperStore';
import {useTabSettingsStore} from '../../store/useTabSettingsStore';
import {Fields} from '../fields';
import {Settings} from '../settings';
import {FieldGroupsTab} from './field-groups-tab';
import {STYLES} from './styles';

export const FieldGroups: FC = () => {
  const {checkTariff, tariffLoading} = useCompanyTariffGuard();
  const id = useStepperStore((s) => s.selectedStepId);
  const {setCanTitleEdit} = useTabSettingsStore();
  const {control, watch} = useStepsFormContext();
  const steps = watch('steps');
  const activeStepIndex = useMemo(() => findIndex(steps, {id: String(id)}), [steps, id]);
  const {append, remove, move} = useFieldArray({control, name: `steps.${activeStepIndex}.fieldGroups`});
  const fields = watch(`steps.${activeStepIndex}.fieldGroups`);
  const [activeTab, setActiveTab] = useState(() => (fields.length > 0 ? fields[0].id : ''));
  const sensors = useSensors(useSensor(PointerSensor));

  useEffect(() => {
    if (fields.length > 0 && !fields.find((f) => f.id === activeTab)) setActiveTab(fields[0].id);
    if (fields.length === 0 && activeTab !== '') setActiveTab('');
    // eslint-disable-next-line
  }, [fields]);

  const handleDragEnd = ({active, over}: DragEndEvent) => {
    if (active.id !== over?.id) {
      const oldIndex = fields.findIndex((step) => step.id === active.id);
      const newIndex = fields.findIndex((step) => step.id === over?.id);

      if (oldIndex !== -1 && newIndex !== -1) move(oldIndex, newIndex);
    }
  };

  const handleAddTab = () => {
    if (!checkTariff()) return;
    const tab = getNewFieldGroup();
    setActiveTab(tab.id);
    append(tab);
    setCanTitleEdit(tab.id, true);
  };

  const handleSelectActiveTab = (_: unknown, value: string) => {
    setActiveTab(value);
  };

  const removeTab = (index: number) => {
    remove(index);
  };

  return (
    <TabContext value={activeTab}>
      <Stack gap="28px" width="100%">
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={4} width="100%">
          <Stack sx={STYLES.root} width="100%">
            <Stack sx={STYLES.wrapper}>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
                modifiers={[restrictToHorizontalAxis]}
                autoScroll={{threshold: {x: 0.1, y: 0}}}
              >
                <SortableContext items={fields.map((field) => field.id)}>
                  <TabList sx={STYLES.tabList} onChange={handleSelectActiveTab} variant="scrollable" scrollButtons>
                    {fields.map((tab, i) => (
                      <FieldGroupsTab
                        key={tab.id}
                        value={tab.id}
                        index={i}
                        tab={tab}
                        remove={removeTab}
                        activeStepIndex={activeStepIndex}
                      />
                    ))}
                  </TabList>
                </SortableContext>
              </DndContext>
            </Stack>
          </Stack>
          <Stack>
            <Stack direction="row" alignItems="flex-end" gap={4} sx={STYLES.navigationButtonWrapper}>
              <IconButton onClick={handleAddTab} disabled={tariffLoading} sx={STYLES.button}>
                <AddIcon /> &nbsp; Добавить группу
              </IconButton>
            </Stack>
          </Stack>
        </Stack>
        <Stack>
          {fields.map((tab, index) => (
            <TabPanel key={tab.id} value={tab.id} sx={{p: 0}}>
              <Stack gap="30px">
                <Settings />
                <Fields index={index} />
              </Stack>
            </TabPanel>
          ))}
        </Stack>
      </Stack>
    </TabContext>
  );
};
